import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, useTheme } from '@mui/material';
import { calculateMeetingLayout, MEETING_GAP, MEETING_PADDING } from './meeting-layout.js';
import { useContentSize } from './useContentSize.js';

export default function MeetingGrid({ active, count, children, stageTarget }) {
    const theme = useTheme();
    const elementRef = useRef(null);
    const [target, setTarget] = useState(null);

    useEffect(() => {
        if (!active) {
            setTarget(null);
            return undefined;
        }
        const findStage = () => stageTarget || document.querySelector('[data-classroom-stage="true"]');
        const current = findStage();
        if (current) {
            setTarget(current);
            return undefined;
        }
        // Observe only while waiting for a late-mounted stage, not every chat update.
        const observer = new MutationObserver(() => {
            const stage = findStage();
            if (stage) {
                observer.disconnect();
                setTarget(stage);
            }
        });
        observer.observe(document.body, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, [active, stageTarget]);

    const size = useContentSize(elementRef, Boolean(active && target), target);
    const layout = useMemo(() => calculateMeetingLayout(size.width, size.height, count), [size.width, size.height, count]);
    if (!active || !target) return null;
    return createPortal(
        <Box
            ref={elementRef}
            data-testid="meeting-grid"
            data-columns={layout?.columns || 0}
            sx={{
                position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 20,
                display: 'grid',
                gridTemplateColumns: layout ? `repeat(${layout.columns}, ${layout.cardWidth}px)` : '1fr',
                gridAutoRows: layout ? `${layout.cardHeight}px` : 'minmax(0, 1fr)',
                gap: `${MEETING_GAP}px`, p: `${MEETING_PADDING}px`, boxSizing: 'border-box',
                alignContent: 'center', justifyContent: 'center', overflow: 'auto',
                backgroundColor: theme.palette.background.gray,
                '@media (max-width: 900px)': { p: 1 },
            }}
        >
            {layout ? children(layout) : null}
        </Box>,
        target,
    );
}
